import { supabase } from '../supabase';
import { handleError } from './db-utils';

export type TaskStatus = 'todo' | 'in_progress' | 'complete' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface TaskRow {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string | null;
  assigned_to: string | null;
  sort_order: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Overall project completion derived from its tasks: completed ÷ non-cancelled,
 * rounded to a whole percent. Cancelled tasks are excluded from both numerator
 * and denominator so abandoning work doesn't inflate progress.
 *
 * Returns `null` when there are no active (non-cancelled) tasks, signalling the
 * caller to fall back to a manually-set completion value rather than showing 0%.
 */
export const deriveCompletionFromTasks = (
  tasks: Pick<TaskRow, 'status'>[]
): number | null => {
  const active = tasks.filter((t) => t.status !== 'cancelled');
  if (active.length === 0) return null;
  const complete = active.filter((t) => t.status === 'complete').length;
  return Math.round((complete / active.length) * 100);
};

interface CreateTaskOptions {
  title: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  assignedTo?: string | null;
  sortOrder?: number;
}

interface UpdateTaskOptions {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  assignedTo?: string | null;
  sortOrder?: number;
}

/**
 * Get all tasks for a project
 */
export const getProjectTasks = async (projectId: string): Promise<TaskRow[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) handleError(error, 'fetch tasks');
    return (data || []) as TaskRow[];
  } catch (error) {
    handleError(error, 'fetch tasks');
    return [];
  }
};

/**
 * Create a new task
 */
export const createTask = async (
  projectId: string,
  options: CreateTaskOptions
): Promise<TaskRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('tasks')
      .insert({
        project_id: projectId,
        title: options.title,
        description: options.description || null,
        status: options.status || 'todo',
        priority: options.priority || 'medium',
        due_date: options.dueDate || null,
        assigned_to: options.assignedTo || null,
        sort_order: options.sortOrder ?? 0,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) handleError(error, 'create task');
    if (!data) throw new Error('No data returned from create');
    return data as TaskRow;
  } catch (error) {
    handleError(error, 'create task');
    throw error;
  }
};

/**
 * Create multiple tasks at once (for AI generation)
 */
export const createTasks = async (
  projectId: string,
  tasks: CreateTaskOptions[]
): Promise<TaskRow[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const rows = tasks.map((t, i) => ({
      project_id: projectId,
      title: t.title,
      description: t.description || null,
      status: t.status || 'todo' as TaskStatus,
      priority: t.priority || 'medium' as TaskPriority,
      due_date: t.dueDate || null,
      assigned_to: t.assignedTo || null,
      sort_order: t.sortOrder ?? i,
      created_by: user.id,
    }));

    const { data, error } = await supabase
      .from('tasks')
      .insert(rows)
      .select();

    if (error) handleError(error, 'create tasks');
    return (data || []) as TaskRow[];
  } catch (error) {
    handleError(error, 'create tasks');
    throw error;
  }
};

/**
 * Update a task
 */
export const updateTask = async (
  taskId: string,
  options: UpdateTaskOptions
): Promise<TaskRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (options.title !== undefined) updateData.title = options.title;
    if (options.description !== undefined) updateData.description = options.description;
    if (options.status !== undefined) updateData.status = options.status;
    if (options.priority !== undefined) updateData.priority = options.priority;
    if (options.dueDate !== undefined) updateData.due_date = options.dueDate;
    if (options.assignedTo !== undefined) updateData.assigned_to = options.assignedTo;
    if (options.sortOrder !== undefined) updateData.sort_order = options.sortOrder;

    const { data, error } = await supabase
      .from('tasks')
      .update(updateData)
      .eq('id', taskId)
      .select()
      .single();

    if (error) handleError(error, 'update task');
    if (!data) throw new Error('No data returned from update');
    return data as TaskRow;
  } catch (error) {
    handleError(error, 'update task');
    throw error;
  }
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) handleError(error, 'delete task');
  } catch (error) {
    handleError(error, 'delete task');
    throw error;
  }
};
